import React, { useState } from 'react';
import { Building2, Eye, EyeOff } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { $api } from '../../api';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';
import { useAuth } from '../../App';

interface LoginProps {
  onLogin?: (token: string) => void | Promise<void>;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const successMessage = location.state?.message;
  const from = location.state?.from?.pathname || '/org/dashboard';
  const [email, setEmail] = useState(import.meta.env.VITE_TEST_EMAIL || '');
  const [password, setPassword] = useState(import.meta.env.VITE_TEST_PASSWORD || '');
  const [mfaToken, setMfaToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [validationError, setValidationError] = useState('');
  const { handleLogin } = useAuth();
  onLogin = onLogin ?? handleLogin;

  // useMutation for login
  const loginMutation = $api.useMutation('post', '/api/auth/login');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    
    loginMutation.mutate(
      {
        body: {
          email,
          password,
          ...(mfaToken && { mfa_token: mfaToken }),
        },
      },
      {
        onSuccess: async (data) => {
          // Check if MFA is required
          if (data.mfa_required) {
            setRequiresMfa(true);
            return;
          }

          // Login successful - save token and call onLogin
          if (data.token) {
            localStorage.setItem('auth_token', data.token);
            await onLogin(data.token);
            // Redirect to the originally requested page
            navigate(from, { replace: true });
          } else {
            setValidationError('Token non reçu du serveur');
          }
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-3 rounded-full">
              <Building2 className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">KYC Pro</h1>
          <p className="text-gray-600">Plateforme d'automatisation du screening KYC</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {successMessage && (
            <SuccessMessage message={successMessage} className="mb-4" />
          )}

          {(validationError || loginMutation.error) && (
            <ErrorMessage error={validationError || loginMutation.error} className="mb-4" />
          )}

          {requiresMfa && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-600 text-sm">
              Veuillez entrer votre code d'authentification à deux facteurs
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="votre@email.com"
                autoComplete="email"
                required
                disabled={requiresMfa}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  disabled={requiresMfa}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={requiresMfa}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {requiresMfa && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code MFA
                </label>
                <input
                  type="text"
                  value={mfaToken}
                  onChange={(e) => setMfaToken(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="123456"
                  required
                  autoFocus
                  maxLength={6}
                />
              </div>
            )}

            {!requiresMfa && (
              <div className="flex items-center justify-between">
                <Link to="/auth/password-lost" className="text-sm text-blue-600 hover:text-blue-500">
                  Mot de passe oublié ?
                </Link>
              </div>
            )}

            {requiresMfa && (
              <div className="text-center">
                <Link to="/auth/mfa-lost" className="text-sm text-blue-600 hover:text-blue-500">
                  Perdu l'accès à votre MFA ?
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loginMutation.isPending ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Connexion...
                </div>
              ) : (
                requiresMfa ? 'Vérifier le code MFA' : 'Se connecter'
              )}
            </button>

            {requiresMfa && (
              <button
                type="button"
                onClick={() => {
                  setRequiresMfa(false);
                  setMfaToken('');
                  setValidationError('');
                  loginMutation.reset();
                }}
                className="w-full text-blue-600 hover:text-blue-700 text-sm"
              >
                Retour
              </button>
            )}

            {!requiresMfa && (
              <div className="text-center text-sm text-gray-600">
                Pas encore de compte ?{' '}
                <Link to="/auth/signup" className="text-blue-600 hover:text-blue-500 font-medium">
                  S'inscrire
                </Link>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>&copy; 2025 KYC Pro. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;